import React, { useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Flex,
  GridItem,
  HStack,
  Icon, Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IconButtonMenu from '../../../../components/IconButtonMenu';
import IntegrationExternalIdStack from '../../../../components/IntegrationExternalIdStack';
import JobStatusIcon from '../../../../components/JobStatusIcon';
import Row from '../../../../components/Row';
import RowGridColumn from '../../../../components/Row/components/RowGridColumn';
import StatusBadge from '../../../../components/StatusBadge';
import { MultiLineTruncatedCell } from '../../../../components/Text/MultiLineTruncatedCell';
import { CypressIds } from '../../../../constants/cypressIds';
import { ListInsuredsPageDuskIds } from '../../../../constants/duskIds';
import { Statuses } from '../../../../constants/statuses';
import { selectInsuredUpdating } from '../../../../redux/modules/insured';
import colors from '../../../../styles/theme/foundation/colors';
import { arrayFilterAndMap, get } from '../../../../utils/arrayUtil';

import { activeMenuOptions, archivedMenuOptions } from './util';

const MAX_RECORDS_DISPLAY = 5;

const InsuredListRow = ({ insured }) => {
  const updatingInsured = useSelector(selectInsuredUpdating);
  const isArchived = insured.overallStatus === Statuses.Archived;
  const menuOptions = isArchived ? archivedMenuOptions : activeMenuOptions;

  const insuredIntegrations = useMemo(() => insured?.insuredIntegrations ?? [], [insured]);

  const records = useMemo(() => arrayFilterAndMap(
    insured?.insuranceRequirements ?? [],
    requirement => requirement.status !== Statuses.Archived,
    requirement => {
      const duskSelector = ListInsuredsPageDuskIds.getInsuredRecordRow(requirement.id);

      return (
        <HStack key={requirement.id} maxWidth="100%">
          <JobStatusIcon status={requirement.status} />
          <Text
            maxWidth="100%"
            overflow="hidden"
            textOverflow="ellipsis"
            textStyle="bodyMd500"
            whiteSpace="nowrap"
          >
            <Text
              as="span"
              color="greyScale.900"
              data-dusk-value={requirement.status}
              dusk={duskSelector}
            >
              {requirement.job.name}
            </Text>
            {!!requirement.job.number && (
            <Text as="span" color="greyScale.600">{` (${requirement.job.number})`}</Text>)}
          </Text>
        </HStack>
      );
    }
  ), [insured]);

  const renderRow = ({ clickable, layerStyle }) => (
    <Row
      clickable={clickable}
      columns="repeat(14, 1fr)"
      data-cy={CypressIds.listInsuredsRow(insured.companyName)}
      height="82px"
      layerStyle={layerStyle}
      mt={4}
      textAlign="center"
      textStyle="bodyMd"
    >
      <GridItem colSpan={3} h="100%" pl={5}>
        <RowGridColumn align="flex-start" label="Insured Company Name">
          <MultiLineTruncatedCell maxLength={64} text={insured.companyName} />
        </RowGridColumn>
      </GridItem>

      <GridItem colSpan={2} h="100%" pl={5}>
        <Text
          color={colors.greyScale['700']}
          mb={1}
          position="relative"
          textAlign="left"
          textStyle="bodySm500"
        >
          Insured External Ids
        </Text>
        <IntegrationExternalIdStack
          customExternalId={insured?.externalId}
          integrations={insuredIntegrations}
          resourceName="insuredId"
        />
      </GridItem>

      <GridItem colSpan={4} h="100%" pl={5}>
        <RowGridColumn align="flex-start" label="Primary Contact">
          <Tooltip hasArrow={false} label={insured.email || 'No Contact Info Present'}>
            <Text
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {insured.email || ' - '}
            </Text>
          </Tooltip>
        </RowGridColumn>
      </GridItem>
      <GridItem colSpan={4} h="100%" pl={5} width="100%">
        <RowGridColumn align="flex-start" label="Projects" width="100%">
          <VStack alignItems="flex-start" maxWidth="100%">
            {isArchived
              ? <StatusBadge minW="200px" size="sm" status={Statuses.Archived} />
              : records.slice(0, MAX_RECORDS_DISPLAY)}
            {records.length > MAX_RECORDS_DISPLAY && (
              <Popover trigger="hover">
                <PopoverTrigger>
                  <Text color="greyScale.600" textStyle="bodyMd500">
                    {`+${records.length - MAX_RECORDS_DISPLAY} more`}
                  </Text>
                </PopoverTrigger>
                <PopoverContent p={2}>
                  {records.slice(MAX_RECORDS_DISPLAY)}
                </PopoverContent>
              </Popover>
            )}
          </VStack>
        </RowGridColumn>
      </GridItem>
      <GridItem colSpan={1} height="100%">
        <Tooltip label="Insured Options" placement="top">
          <Flex align="center" direction="column" height="100%" justify="center">
            <IconButtonMenu
              ariaLabel="Insured Options Menu"
              iconElement={<Icon as={BsThreeDots} boxSize="20px" px={0} />}
              id={insured.id}
              isLoading={updatingInsured}
              menuOptions={menuOptions}
              size="sm"
              width="auto"
            />
          </Flex>
        </Tooltip>
      </GridItem>
    </Row>
  );

  return isArchived
    ? renderRow({ clickable: false, layerStyle: 'secondaryArchived' })
    : (
      <Link
        data-analytics-id="list-insureds-insured-row"
        dusk="list-insureds-insured-row"
        style={{
          textDecoration: 'none',
          _hover: { textDecoration: 'none' },
          ...(updatingInsured && { pointerEvents: 'none' })
        }}
        to={location => (`${location.pathname}/${insured.id}${get(insured, 'insuranceRequirements.0.status') !== Statuses.Archived
          ? `/records/${get(insured, 'insuranceRequirements.0.id')}` : ''}`)}
      >
        {renderRow({ clickable: true, layerStyle: 'row' })}
      </Link>
    );
};

InsuredListRow.propTypes = {
  insured: PropTypes.shape({
    id: PropTypes.number,
    externalId: PropTypes.string,
    companyName: PropTypes.string,
    status: PropTypes.string,
    email: PropTypes.string,
    overallStatus: PropTypes.string,
    insuranceRequirements: PropTypes.arrayOf(PropTypes.shape({
      job: PropTypes.shape({
        name: PropTypes.string,
        number: PropTypes.string
      }),
      status: PropTypes.string
    })),
    insuredIntegrations: PropTypes.arrayOf(PropTypes.shape({
      externalId: PropTypes.string,
      integrationTypeName: PropTypes.string,
      thumbnailUrl: PropTypes.string,
      insuredId: PropTypes.number
    }))
  })
};

export default InsuredListRow;
